import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config/apiConfig';
import { LoadingSpinner } from '../../components/general/LoadingSpinner';
import { FaDollarSign, FaMap, FaChargingStation, FaTruck, FaArrowRight } from 'react-icons/fa';

const DataHubHome = () => {
  const navigate = useNavigate();
  const authToken = localStorage.getItem('authToken');
  
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(API_ENDPOINTS.dataHubOverview, {
        headers: { Authorization: `Token ${authToken}` },
      });

      setOverview(response.data);
    } catch (err) {
      console.error('Error fetching DataHub overview:', err);
      setError('Failed to load DataHub overview. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  const getIconComponent = (iconName) => {
    switch (iconName) {
      case 'dollar':
        return FaDollarSign;
      case 'map':
        return FaMap;
      case 'charging':
        return FaChargingStation;
      case 'truck':
        return FaTruck;
      default:
        return FaMap;
    }
  };

  const getCardColor = (iconName) => {
    switch (iconName) {
      case 'dollar':
        return 'from-green-500 to-green-600';
      case 'map':
        return 'from-blue-500 to-blue-600';
      case 'charging':
        return 'from-purple-500 to-purple-600';
      case 'truck':
        return 'from-orange-500 to-orange-600';
      default:
        return 'from-gray-500 to-gray-600';
    }
  };

  const getNavigationPath = (modelKey) => {
    switch (modelKey) {
      case 'utility_rates':
        return '/data/rates';
      case 'utility_territories':
        return '/data/territories';
      case 'charging_stations_doe':
      case 'charging_stations_calstart':
        return '/data/charging-stations';
      case 'vehicle_models':
        return '/data/vehicles';
      default:
        return '/data';
    }
  };

  const getCardTitle = (modelKey) => {
    switch (modelKey) {
      case 'utility_rates':
        return 'Utility Rates';
      case 'utility_territories':
        return 'Utility Territories';
      case 'charging_stations_doe':
        return 'EV Charging Stations (DOE)';
      case 'charging_stations_calstart':
        return 'Heavy-Duty Charging (CalSTART)';
      case 'vehicle_models':
        return 'Vehicle Models';
      default:
        return 'Unknown';
    }
  };

  const renderDataCard = (modelKey, modelData) => {
    const IconComponent = getIconComponent(modelData.icon);
    const colorClasses = getCardColor(modelData.icon);
    const title = getCardTitle(modelKey);
    const path = getNavigationPath(modelKey);

    return (
      <div
        key={modelKey}
        onClick={() => navigate(path)}
        className="bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer overflow-hidden group"
      >
        {/* Header with gradient */}
        <div className={`bg-gradient-to-r ${colorClasses} p-6 text-white relative overflow-hidden`}>
          <div className="absolute top-0 right-0 opacity-10 transform translate-x-4 -translate-y-4">
            <IconComponent className="text-9xl" />
          </div>
          <div className="relative z-10">
            <IconComponent className="text-4xl mb-3" />
            <h3 className="text-2xl font-bold mb-1">{title}</h3>
            <p className="text-sm opacity-90">
              {modelData.has_geo ? 'Geographic data available' : 'Tabular data'}
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="mb-4">
            <div className="text-4xl font-bold text-gray-800 mb-2">
              {modelData.count.toLocaleString()}
            </div>
            <div className="text-sm text-gray-600">Records available</div>
          </div>

          <p className="text-gray-700 mb-4 min-h-[3rem]">
            {modelData.description}
          </p>

          <div className="flex items-center text-blue-600 font-medium group-hover:text-blue-700 transition-colors">
            <span>Explore Data</span>
            <FaArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  return (
    <div className="min-h-screen bg-gray-50 p-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-800 font-ui mb-3">DataHub</h1>
            <p className="text-xl text-gray-600 font-ui">
              Explore comprehensive datasets for electric vehicle infrastructure and utility planning
            </p>
          </div>

          {/* Loading State */}
          {loading && <LoadingSpinner message="Loading DataHub..." />}

          {/* Error State */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
              <p className="text-red-600 font-medium">{error}</p>
              <button
                onClick={fetchOverview}
                className="mt-4 px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
              >
                Try Again
              </button>
            </div>
          )}

          {/* Data Cards Grid */}
          {!loading && !error && overview && (
            <>
              {/* Main datasets */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                {overview.utility_rates && renderDataCard('utility_rates', overview.utility_rates)}
                {overview.utility_territories && renderDataCard('utility_territories', overview.utility_territories)}
                {overview.vehicle_models && renderDataCard('vehicle_models', overview.vehicle_models)}
              </div>

              {/* Charging stations - combined section */}
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">EV Charging Infrastructure</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {overview.charging_stations_doe && renderDataCard('charging_stations_doe', overview.charging_stations_doe)}
                  {overview.charging_stations_calstart && renderDataCard('charging_stations_calstart', overview.charging_stations_calstart)}
                </div>
              </div>

              {/* Info Section */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-8">
                <h3 className="text-lg font-semibold text-blue-900 mb-2">About DataHub</h3>
                <p className="text-blue-800">
                  DataHub provides access to curated datasets for electric vehicle infrastructure planning, 
                  utility analysis, and fleet electrification. All geographic data can be visualized on 
                  interactive maps, while tabular data is available in searchable, sortable tables.
                </p>
              </div>
            </>
          )}
    </div>
  );
};

export default DataHubHome;
